/**
 * Token Refresh Service
 * Automatically refreshes authentication tokens from session storage at regular intervals
 * to prevent session timeout while user is actively working
 * 
 * Author: fabrice.miras@vitecsoftware.com
 * Date: 2026-03-28
 */

import UserCred from '../models/UserCred';
import { getStore } from '../store/_-_-_-_-__-_-_-_-__configureStore';
import * as storeActions from '../store/actions/index';
import { autoLoginToAutodataDotNetAPI } from './APICalls_NET';
import { tabCommunication } from './TabCommunicationService';
import Utils from './Utils';

// Store instance for dispatching logout on invalid token
let storeInstance: any = null;



export interface TokenRefreshConfig {
  /**
   * Interval in milliseconds between token refreshes
   * Default: 3600000 (1 hour)
   */
  refreshInterval?: number;

  /**
   * Whether to perform the first refresh immediately
   * Default: false
   */
  immediateRefresh?: boolean;

  /**
   * Whether to log refresh activities to console (development only)
   * Default: false
   */
  enableLogging?: boolean;

  /**
   * Maximum inactivity time in milliseconds before logging out
   * Default: 21600000 (6 hours)
   * Set to 0 to disable auto-logout
   */
  maxInactivityTime?: number;

  /**
   * Warning time in milliseconds before maxInactivityTime triggers logout
   * A warning popup will be shown this many ms before logout
   * Default: 120000 (2 minutes)
   */
  warningBeforeLogout?: number;

  /**
   * Callback fired when the inactivity warning should be displayed
   * Receives the number of milliseconds remaining before logout
   */
  onInactivityWarning?: (remainingMs: number) => void;

  /**
   * Callback fired when the inactivity warning is dismissed (user resumed activity)
   */
  onInactivityWarningDismissed?: () => void;

  /**
   * Callback fired when inactivity logout is triggered
   */
  onInactivityLogout?: () => void;
}

export class TokenRefreshService {
  private intervalId: NodeJS.Timeout | null = null;
  private config: Required<TokenRefreshConfig>;
  private isRefreshing = false;
  private isPaused = false;
  private lastActivityTime: number = Date.now();
  private activityCheckIntervalId: NodeJS.Timeout | null = null;
  private userActivityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
  private isWarningActive = false;
  private warningTimeoutId: NodeJS.Timeout | null = null;
  private lastBroadcastTime = 0;
  private static readonly HEARTBEAT_THROTTLE_MS = 30000; // Broadcast activity to other tabs at most every 30 seconds

  /**
   * Default configuration: refresh every hour, pause after 6 hours of inactivity
   * 0 = Never pause due to inactivity
   * 14400000 = 4 hours
   * 21600000 = 6 hours
   * 
   */
  private static readonly DEFAULT_CONFIG: Required<TokenRefreshConfig> = {
    refreshInterval: 3600000, // 1 hour in milliseconds
    immediateRefresh: false,
    enableLogging: false,
    maxInactivityTime: 21600000, // 6 hours in milliseconds
    warningBeforeLogout: 120000, // 2 minutes in milliseconds
    onInactivityWarning: () => {},
    onInactivityWarningDismissed: () => {},
    onInactivityLogout: () => {},
  };

  constructor(config: TokenRefreshConfig = {}) {
    this.config = {
      ...TokenRefreshService.DEFAULT_CONFIG,
      ...config
    };
  }

  /**
   * Initialize the service with the Redux store instance
   * Required for dispatching logout when token is invalid
   * @param store - The Redux store instance
   */
  initialize(store: any): void {
    storeInstance = store;
    this.log('Token refresh service initialized with store');
  }

  /**
   * Update last activity timestamp
   */
  private updateActivity(): void {
    this.lastActivityTime = Date.now();
  }

  /**
   * Handle user activity events
   * If a warning is active, dismiss it. If the service is paused, resume it.
   * Also broadcasts a throttled heartbeat to keep other tabs alive.
   */
  private handleUserActivity = (): void => {
    this.updateActivity();
    this.broadcastActivityHeartbeat();
    if (this.isWarningActive) {
      this.dismissWarning();
    }
    if (this.isPaused) {
      this.resume();
    }
  };

  /**
   * Broadcast activity heartbeat to other tabs (throttled)
   * This keeps other tabs alive as long as the user is active in any tab
   */
  private broadcastActivityHeartbeat(): void {
    const now = Date.now();
    if (now - this.lastBroadcastTime >= TokenRefreshService.HEARTBEAT_THROTTLE_MS) {
      this.lastBroadcastTime = now;
      tabCommunication.broadcast({ command: 'ACTIVITY_HEARTBEAT' });
    }
  }

  /**
   * Check if user has been inactive for too long
   * Shows a warning before logout, then triggers logout
   */
  private checkInactivity(): void {
    if (this.config.maxInactivityTime === 0) {
      return; // Auto-logout disabled
    }

    const inactiveTime = Date.now() - this.lastActivityTime;
    const warningThreshold = this.config.maxInactivityTime - this.config.warningBeforeLogout;

    // Trigger logout when max inactivity is reached
    if (inactiveTime >= this.config.maxInactivityTime) {
      this.log(`Max inactivity reached (${Math.round(inactiveTime / 60000)} minutes) — triggering logout`);
      this.performInactivityLogout();
      return;
    }

    // Show warning when approaching max inactivity
    if (inactiveTime >= warningThreshold && !this.isWarningActive) {
      const remainingMs = this.config.maxInactivityTime - inactiveTime;
      this.log(`Inactivity warning: ${Math.round(remainingMs / 1000)} seconds until logout`);
      this.isWarningActive = true;
      this.config.onInactivityWarning(remainingMs);
    }
  }

  /**
   * Dismiss the inactivity warning (user resumed activity)
   */
  private dismissWarning(broadcast = true): void {
    if (this.isWarningActive) {
      this.isWarningActive = false;
      if (this.warningTimeoutId) {
        clearTimeout(this.warningTimeoutId);
        this.warningTimeoutId = null;
      }
      this.config.onInactivityWarningDismissed();
      this.log('Inactivity warning dismissed — user resumed activity');

      // Notify other tabs to also resume and refresh their token
      if (broadcast) {
        tabCommunication.broadcast({ command: 'INACTIVITY_RESUME' });
        this.log('Broadcast INACTIVITY_RESUME to other tabs');
      }
    }
  }

  /**
   * Perform logout due to inactivity
   */
  private performInactivityLogout(): void {
    this.isWarningActive = false;
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
    this.stop();
    this.config.onInactivityLogout();

    // Dispatch Redux logout
    const store = storeInstance || getStore();
    if (store) {
      store.dispatch(storeActions.UserLogoutFetchValidation(
        new UserCred('INACTIVITY_LOGOUT', 'INACTIVITY_LOGOUT', '', false),
        true,  // performSilent
        0,     // isLogged
        -1,    // loginStateUI
        false, // isAlreadyLoggedFromAnotherTab
        1,     // tabListSize
        true   // activeTab
      ));
      this.log('Inactivity logout dispatched');
    }
  }

  /**
   * Register user activity listeners
   */
  private registerActivityListeners(): void {
    this.userActivityEvents.forEach(event => {
      window.addEventListener(event, this.handleUserActivity, { passive: true });
    });
    this.log('Activity listeners registered');
  }

  /**
   * Unregister user activity listeners
   */
  private unregisterActivityListeners(): void {
    this.userActivityEvents.forEach(event => {
      window.removeEventListener(event, this.handleUserActivity);
    });
    this.log('Activity listeners unregistered');
  }

  /**
   * Start the automatic token refresh service
   */
  start(): void {
    if (this.intervalId) {
      this.log('Token refresh service is already running');
      return;
    }

    this.log(`Starting token refresh service (interval: ${this.config.refreshInterval}ms, max inactivity: ${this.config.maxInactivityTime}ms)`);

    // Reset activity timestamp
    this.updateActivity();

    // Listen for cross-tab resume broadcasts
    this.registerTabCommunication();

    // Register activity listeners if inactivity check is enabled
    if (this.config.maxInactivityTime > 0) {
      this.registerActivityListeners();

      // Start periodic inactivity check (every 10 seconds for responsive warning detection)
      const checkInterval = Math.min(this.config.warningBeforeLogout, 10000);
      this.activityCheckIntervalId = setInterval(() => {
        this.checkInactivity();
      }, checkInterval);
    }

    // Perform immediate refresh if configured
    if (this.config.immediateRefresh) {
      this.refreshToken();
    }

    // Set up recurring refresh
    this.intervalId = setInterval(() => {
      this.refreshToken();
    }, this.config.refreshInterval);
  }

  /**
   * Stop the automatic token refresh service
   */
  stop(): void {
    if (this.intervalId) {
      this.log('Stopping token refresh service');
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.activityCheckIntervalId) {
      clearInterval(this.activityCheckIntervalId);
      this.activityCheckIntervalId = null;
    }

    this.isPaused = false;
    this.dismissWarning();

    // Unregister activity listeners
    this.unregisterActivityListeners();
    this.unregisterTabCommunication();
  }

  /**
   * Register cross-tab communication listener
   * When another tab broadcasts INACTIVITY_RESUME, this tab also resumes
   */
  private registerTabCommunication(): void {
    tabCommunication.onMessage('INACTIVITY_RESUME', () => {
      this.log('Received INACTIVITY_RESUME from another tab');
      this.updateActivity();
      this.dismissWarning(false); // false = don't re-broadcast
      this.refreshToken();
    });
    tabCommunication.onMessage('ACTIVITY_HEARTBEAT', () => {
      this.updateActivity();
      // If a warning is showing but another tab is active, dismiss it
      if (this.isWarningActive) {
        this.log('Received ACTIVITY_HEARTBEAT from another tab — dismissing warning');
        this.dismissWarning(false);
      }
    });
  }

  /**
   * Unregister cross-tab communication listener
   */
  private unregisterTabCommunication(): void {
    tabCommunication.offMessage('INACTIVITY_RESUME');
    tabCommunication.offMessage('ACTIVITY_HEARTBEAT');
  }

  /**
   * Pause the token refresh (stop refreshing but keep activity listeners active)
   * Service will resume automatically when user activity is detected
   */
  private pause(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.activityCheckIntervalId) {
      clearInterval(this.activityCheckIntervalId);
      this.activityCheckIntervalId = null;
    }
    this.isPaused = true;
    this.log('Token refresh service paused due to inactivity — waiting for user activity to resume');
  }

  /**
   * Resume the token refresh service after being paused
   * Performs an immediate token refresh then restarts the interval
   */
  private resume(): void {
    this.isPaused = false;
    this.log('User activity detected — resuming token refresh service');
    this.updateActivity();

    // Refresh token immediately on resume
    this.refreshToken();

    // Restart the intervals
    this.intervalId = setInterval(() => {
      this.refreshToken();
    }, this.config.refreshInterval);

    if (this.config.maxInactivityTime > 0) {
      const checkInterval = Math.min(this.config.warningBeforeLogout, 10000);
      this.activityCheckIntervalId = setInterval(() => {
        this.checkInactivity();
      }, checkInterval);
    }
  }

  /**
   * Manually trigger a token refresh
   * Calls the API directly and only updates sessionStorage — no Redux dispatches, no re-renders
   * If the server responds with "Invalid token", triggers a full logout via Redux
   */
  async refreshToken(): Promise<void> {
    if (this.isRefreshing) {
      this.log('Token refresh already in progress, skipping');
      return;
    }

    const token = sessionStorage.getItem('token');
    const btsg = sessionStorage.getItem('btsg') || '';

    if (!token) {
      this.log('No token found in session storage, skipping refresh');
      return;
    }

    try {
      this.isRefreshing = true;
      this.log('Refreshing token...');

      // Call the API directly — bypass Redux entirely to avoid re-renders
      const res = await autoLoginToAutodataDotNetAPI(token, btsg);
      if (res && res.data) {
        sessionStorage.setItem('token', res.data.token);
        this.log('Token refreshed successfully in sessionStorage');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || '';
      this.log('Error during token refresh:', errorMessage);

      // If the server responds with "Invalid token", trigger logout
      if (errorMessage.includes('Invalid token')) {
        this.log('Invalid token detected — triggering logout');
        this.stop();
        if (storeInstance) {
          const { UserLogoutFetchValidation } = await import('../store/actions');
          storeInstance.dispatch(UserLogoutFetchValidation({} as any));
        }
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Check if the service is currently running (returns false when paused)
   */
  isRunning(): boolean {
    return this.intervalId !== null;
  }

  /**
   * Update the refresh interval
   * @param newInterval - New interval in milliseconds
   */
  setRefreshInterval(newInterval: number): void {
    this.config.refreshInterval = newInterval;

    if (this.intervalId) {
      this.stop();
      this.start();
      this.log(`Refresh interval updated to ${newInterval}ms`);
    }
  }

  /**
   * Update the maximum inactivity time
   * @param newMaxInactivityTime - New max inactivity time in milliseconds (0 to disable)
   */
  setMaxInactivityTime(newMaxInactivityTime: number): void {
    this.config.maxInactivityTime = newMaxInactivityTime;

    if (this.intervalId) {
      this.stop();
      this.start();
      this.log(`Max inactivity time updated to ${newMaxInactivityTime}ms`);
    }
  }

  /**
   * Get the current configuration
   */
  getConfig(): Required<TokenRefreshConfig> {
    return { ...this.config };
  }

  /**
   * Update configuration (merges with existing config)
   * Useful for setting callbacks after service creation
   */
  setConfig(config: Partial<TokenRefreshConfig>): void {
    this.config = { ...this.config, ...config } as Required<TokenRefreshConfig>;
  }

  /**
   * Log messages (only in development mode if logging is enabled)
   */
  private log(message: string, ...args: any[]): void {
    if (this.config.enableLogging && (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'vm')) {
      const timestamp = new Date().toLocaleTimeString('nb-NO');
      Utils.log(`[TokenRefreshService ${timestamp}]`, message, ...args);
    }
  }

  /**
   * Clean up the service
   */
  destroy(): void {
    this.stop();
    this.log('Token refresh service destroyed');
  }
}



const WARNING_BEFORE_LOGOUT_MINUTES = 5; // How many minutes before maxInactivityTime the warning popup appears

// Singleton instance for the application
// Refresh every hour (3600000ms), stop after 6 hours of inactivity (21600000ms)
export const tokenRefreshService = new TokenRefreshService({
  refreshInterval: 3660000, // 1 hour in milliseconds 
  immediateRefresh: false,
  enableLogging: true, // Enable logging in development
  maxInactivityTime: 3600000, // 1 hour in milliseconds (set to 0 to disable auto-logout)
  warningBeforeLogout: WARNING_BEFORE_LOGOUT_MINUTES * 60 * 1000, // converted to milliseconds
});
  